"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import Icon from "@/components/ui/AppIcon";
import { useLanguage } from "@/contexts/LanguageContext";

interface Language {
  code: string;
  label: string;
}

const languages: Language[] = [
  { code: "es", label: "ES" },
  { code: "en", label: "EN" },
];

interface NavLink {
  id: string;
  href: string;
  labelES: string;
  labelEN: string;
}

const navLinks: NavLink[] = [
  { id: "nav_home", href: "/homepage", labelES: "Inicio", labelEN: "Home" },
  { id: "nav_about", href: "/about", labelES: "Nosotros", labelEN: "About" },
  { id: "nav_services", href: "/services", labelES: "Servicios", labelEN: "Services" },
  { id: "nav_contact", href: "/contact", labelES: "Contacto", labelEN: "Contact" },
  { id: "nav_calculator", href: "/calculator", labelES: "Diagnóstico", labelEN: "Diagnostic" },
];

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { currentLang, toggleLanguage } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActive = (href: string) => pathname === href;

  return (
    <nav
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? "glass-nav border-b border-border shadow-lg" : "bg-background/80 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-24 flex items-center justify-between">
        {/* Logo */}
        <Link href="/homepage" className="flex items-center gap-3 cursor-pointer group">
          <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center text-primary-foreground shadow-xl group-hover:scale-105 transition-transform duration-500">
            <Icon name="ScaleIcon" size={18} variant="outline" />
          </div>
          <div className="flex flex-col justify-center">
            <span className="text-lg font-semibold tracking-tight text-primary leading-none font-serif">
              CARRIZO PEREZ
            </span>
            <span className="text-[9px] uppercase tracking-[0.2em] text-secondary font-medium mt-0.5">
              Abogados
            </span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-1 bg-accent/50 p-1.5 rounded-full border border-border backdrop-blur-sm">
          {navLinks.map((link) => (
            <Link
              key={link.id}
              href={link.href}
              className={`px-6 py-2 rounded-full text-xs font-medium transition-all ${
                isActive(link.href)
                  ? "bg-card shadow-md text-primary border border-border"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {currentLang === "es" ? link.labelES : link.labelEN}
            </Link>
          ))}
        </div>

        {/* CTA + Language Toggle */}
        <div className="flex items-center gap-6">
          {/* Language Toggle */}
          <button
            onClick={toggleLanguage}
            className="hidden md:flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            {languages.map((lang, idx) => (
              <span key={lang.code}>
                <span
                  className={`uppercase tracking-wide ${
                    currentLang === lang.code ? "text-secondary font-semibold" : ""
                  }`}
                >
                  {lang.label}
                </span>
                {idx < languages.length - 1 && <span className="mx-1 text-border">|</span>}
              </span>
            ))}
          </button>

          {/* CTA Button */}
          <Link
            href="/contact"
            className="group bg-secondary hover:bg-secondary/90 text-secondary-foreground text-xs font-medium py-3 px-6 rounded-full transition-all shadow-xl flex items-center gap-3"
          >
            <span>{currentLang === "es" ? "Consulta" : "Consult"}</span>
            <Icon
              name="ArrowRightIcon"
              size={14}
              className="group-hover:translate-x-1 transition-transform"
            />
          </Link>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-primary"
            aria-label="Toggle menu"
          >
            <Icon name={isMenuOpen ? "XMarkIcon" : "Bars3Icon"} size={24} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-card border-t border-border shadow-2xl">
          <div className="px-6 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.id}
                href={link.href}
                onClick={() => setIsMenuOpen(false)}
                className={`block py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                  isActive(link.href)
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                }`}
              >
                {currentLang === "es" ? link.labelES : link.labelEN}
              </Link>
            ))}
            <button
              onClick={toggleLanguage}
              className="w-full py-3 px-4 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors text-left"
            >
              {currentLang === "es" ? "Switch to English" : "Cambiar a Español"}
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}